import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { t } from '../../i18n'
import { Button, Note } from '../../ui'
import { GroupDialog } from './GroupDialog'
import { DEFAULT_GROUP_KEY, adminErrorText, groupAdminApi, isDanger, useDefaultGroup, useDryRun } from './groupAdmin'
import { isNodeGroup } from './groups'
import { FAILOVER_KEY, useFailover } from './NodeFailover'
import { tn } from './nodeSwitch'
import { WarningNotes } from './Warnings'

/*
 * Network › Nodes › Automatic region (NG-17 A; GROUP-DEFAULT #550, owner V1 01.11): the default
 * node group is what users with the automatic region stream to; none = the center's addresses.
 * A select of node groups + Save → ConfirmDialog level 3 with the dry run's warnings first.
 * The center's own warnings about the setting (the group has no live member, is deleted, has no
 * certificate yet…) stand as warn notes over the select. Hidden on a center without GROUP-DEFAULT.
 */

export function DefaultGroupPanel() {
  const qc = useQueryClient()
  const dg = useDefaultGroup()
  const fo = useFailover()
  const cur = dg.data?.group ?? ''
  const [sel, setSel] = useState(cur)
  const [asking, setAsking] = useState(false)
  const [done, setDone] = useState('')
  const trigger = useRef<HTMLElement | null>(null)
  useEffect(() => setSel(cur), [cur])
  const dry = useDryRun(asking ? { method: 'PUT', path: '/api/admin/ingest/default-group', body: { group: sel || null } } : null, dg.supported)
  if (!dg.supported || !dg.data) return null
  const v = dg.data
  const groups = (fo.data?.groups ?? []).filter((g) => isNodeGroup(g)).sort((a, b) => a.name.localeCompare(b.name))
  const chosen = groups.find((g) => g.id === sel)
  const now = groups.find((g) => g.id === cur)
  const name = now?.name ?? v.name ?? cur
  return (
    <section className="sx-panel sx-console flex flex-col gap-3" data-testid="default-group" data-state={v.state}>
      <span className="sx-label">{t('gx.default.title')}</span>
      <WarningNotes warnings={v.warnings} ctx={{ group: name }} testId="default-group-warn" />
      <div className="flex flex-wrap items-end gap-3">
        <div className="sx-field min-w-[240px] flex-1 md:max-w-[420px]">
          <label htmlFor="default-group-select">{t('gx.default.field')}</label>
          <select id="default-group-select" className="sx-select w-full focus:border-accent focus:shadow-focus focus:outline-none max-md:min-h-[44px]" value={sel} onChange={(e) => (setSel(e.target.value), setDone(''))} data-testid="default-group-select">
            <option value="">{t('gx.default.center')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
        <Button className="max-md:min-h-[44px]" disabled={sel === cur} data-testid="default-group-save" onClick={(e) => ((trigger.current = e.currentTarget), setAsking(true))}>
          {t('gx.default.save')}
        </Button>
      </div>
      <span className="text-[12px] text-muted" data-testid="default-group-help">
        {chosen
          ? tn('gx.default.help', { fqdn: chosen.fqdn ?? chosen.name })
          : groups.length
            ? v.automatic.rtmp ? tn('gx.default.helpCenterAt', { rtmp: v.automatic.rtmp }) : t('gx.default.helpCenter')
            : t('gx.default.helpNoGroups')}
      </span>
      {done && (
        <Note role="status" data-testid="default-group-done">
          {done}
        </Note>
      )}
      {asking && (
        <GroupDialog
          testId="default-group"
          title={chosen ? tn('gx.default.confirm', { name: chosen.name }) : t('gx.default.confirmCenter')}
          facts={chosen ? [tn('gx.default.f1', { fqdn: chosen.fqdn ?? chosen.name }), t('gx.default.f2'), t('gx.default.f3')] : [t('gx.default.c1'), t('gx.default.f3')]}
          action={t('gx.default.save')}
          ready={!dry.pending}
          danger={isDanger(dry.warnings)}
          returnFocus={trigger}
          onClose={() => setAsking(false)}
          errorText={(e) => adminErrorText(e, { name: chosen?.name ?? name })}
          submit={async (totp) => {
            await groupAdminApi.setDefault(sel || null, totp)
            setAsking(false)
            setDone(chosen ? t('gx.default.done', { name: chosen.name }) : t('gx.default.doneCenter'))
            await Promise.all([qc.invalidateQueries({ queryKey: DEFAULT_GROUP_KEY }), qc.invalidateQueries({ queryKey: FAILOVER_KEY })])
          }}
        >
          <WarningNotes warnings={dry.warnings} ctx={{ group: chosen?.name ?? name }} testId="default-group-dry" />
        </GroupDialog>
      )}
    </section>
  )
}
